import { Buffer } from 'buffer'
import decompress from 'brotli/decompress'

// Pinned SHA-256 (of the decompressed bytes) for every artifact the installed SDK accepts.
// Imported by relative path because the package's `exports` map hides `dist/`. Reading it
// from node_modules keeps the pins in lockstep with the installed SDK on every bump.
import ARTIFACT_HASHES from '../../../node_modules/@railgun-community/wallet/dist/services/artifacts/json/artifact-v2-hashes.json'

// The SDK pins no vkey hashes; `yarn fetch-railgun-artifacts` writes these from the verified
// bundle (SHA-256 of key-sorted JSON) and its `--check` fails the build when they drift.
import VKEY_PINS from './railgunVkeyPins.json'

/**
 * Where RAILGUN proving artifacts can be downloaded from, and how to verify them.
 *
 * The SDK downloads from a single hardcoded gateway (`ipfs-lb.com`). When that gateway is
 * down no proof can be generated on a device that has not cached the circuit yet — private
 * sends, unshields and Proof of Innocence submissions all fail. The artifact stores call
 * `fetchVerifiedArtifact` first; it walks the gateways below and accepts a file only when its
 * SHA-256 matches the SDK's pinned hash, so a gateway can only fail, never substitute content.
 *
 * The CIDs must match `MASTER_IPFS_HASH_ARTIFACTS` / `IPFS_HASH_ARTIFACTS_POI` in the SDK's
 * `artifact-util.js`; `yarn ensure-railgun-artifacts` fails the build when they drift.
 */
export const RAILGUN_MASTER_ARTIFACTS_CID = 'QmUsmnK4PFc7zDp2cmC4wBZxYLjNyRgWfs5GNcJJ2uLcpU'
export const RAILGUN_POI_ARTIFACTS_CID = 'QmZ2MyM6TKxffkv6stuo2hFwmUfs3q4xgMYN164Sje8new'

// Public gateways often fail to resolve the POI directory CID while serving its files by
// their own CIDs. Hash-verified like everything else, so the source of this table is not
// trusted.
const POI_FILE_CIDS: Record<string, Partial<Record<VerifiedArtifactKind, string>>> = {
  POI_3x3: {
    zkey: 'QmYG6a72rcLiddX1DyHmDvRaDpkPAf3VdHNNPxVBqndhEy',
    wasm: 'QmaNNJxu5DqzmB3W6BSR49bsPM1MNhwWH9o4Dqd8cSpeyL'
  },
  POI_13x13: {
    zkey: 'QmTY2yzKjMhjdcgC52Q3aiHE5wNRUsWgdDtzFtqSFfuzXm',
    wasm: 'QmUtQFFYcmooihjn5wfYdVNPbtkEv4SKqegwcdcd1L3K5X'
  }
}

// Tried in order: ZKX's CDN mirror (same /ipfs/<CID>/… layout, geo-blocked in some
// countries), the SDK's own gateway, then a public fallback.
export const RAILGUN_ARTIFACT_GATEWAYS = [
  'https://cdn.libertyswap.finance/railgun-zkey',
  'https://ipfs-lb.com',
  'https://gateway.pinata.cloud'
]

const FETCH_TIMEOUT_MS = 40_000

export type VerifiedArtifactKind = 'zkey' | 'wasm'

type HashTable = Record<string, Partial<Record<'zkey' | 'wasm' | 'dat', string>>>

export function getExpectedArtifactHash(
  variant: string,
  kind: VerifiedArtifactKind
): string | undefined {
  return (ARTIFACT_HASHES as HashTable)[variant]?.[kind]
}

/**
 * Cache-key suffix that changes whenever the circuit changes. SDK 10 stored POI artifacts
 * under a fixed path (`poi-nov-2-23/`) and SDK 11 regenerated those circuits, so a key built
 * from the store path alone can serve an old proving key next to a new circuit.
 */
export function getArtifactCacheVersion(variant: string): string {
  return (getExpectedArtifactHash(variant, 'zkey') ?? 'unpinned').slice(0, 16)
}

export function isPoiVariant(variant: string): boolean {
  return variant.startsWith('POI_')
}

// Inside the POI CID the circuits use the main-circuit layout (`circuits/03x03/zkey.br`),
// same as `POI_CIRCUIT_DIRS` in the SDK's artifact-util.js.
const POI_CIRCUIT_DIRS: Record<string, string> = { POI_3x3: '03x03', POI_13x13: '13x13' }

type ArtifactFile = VerifiedArtifactKind | 'vkey'

function getDirectoryUrls(variant: string, file: ArtifactFile): string[] {
  const isPoi = isPoiVariant(variant)
  const cid = isPoi ? RAILGUN_POI_ARTIFACTS_CID : RAILGUN_MASTER_ARTIFACTS_CID
  const dir = isPoi ? POI_CIRCUIT_DIRS[variant] : variant
  if (!dir) return []
  const filePath =
    file === 'wasm'
      ? `prover/snarkjs/${dir}.wasm.br`
      : `circuits/${dir}/${file === 'zkey' ? 'zkey.br' : 'vkey.json'}`
  return RAILGUN_ARTIFACT_GATEWAYS.map((gateway) => `${gateway}/ipfs/${cid}/${filePath}`)
}

function getArtifactUrls(variant: string, kind: VerifiedArtifactKind): string[] {
  const urls = getDirectoryUrls(variant, kind)
  const fileCid = POI_FILE_CIDS[variant]?.[kind]
  if (fileCid) {
    for (const gateway of RAILGUN_ARTIFACT_GATEWAYS) urls.push(`${gateway}/ipfs/${fileCid}`)
  }
  return urls
}

/** Directory URLs for a variant's verification key (vkeys have no pinned hash). */
export function getVkeyUrls(variant: string): string[] {
  return getDirectoryUrls(variant, 'vkey')
}

async function fetchWithTimeout(url: string): Promise<ArrayBuffer> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined
  const timer = setTimeout(() => controller?.abort(), FETCH_TIMEOUT_MS)
  try {
    const res = await fetch(url, { signal: controller?.signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.arrayBuffer()
  } finally {
    clearTimeout(timer)
  }
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(data))
  return Buffer.from(digest).toString('hex')
}

// Key-sorted JSON so formatting differences between sources hash the same.
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    return `{${Object.keys(obj)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

/** True when `text` is the verification key pinned for `variant`. */
export async function isPinnedVkey(variant: string, text: string): Promise<boolean> {
  const expected = (VKEY_PINS as Record<string, string>)[variant]
  if (!expected) return false
  try {
    const canonical = canonicalJson(JSON.parse(text))
    return (await sha256Hex(Buffer.from(canonical, 'utf8'))) === expected
  } catch {
    return false
  }
}

/** First fallback source serving the pinned verification key, or null. */
export async function fetchVerifiedVkey(variant: string): Promise<string | null> {
  for (const url of getVkeyUrls(variant)) {
    try {
      const text = Buffer.from(await fetchWithTimeout(url)).toString('utf8')
      if (await isPinnedVkey(variant, text)) return text
    } catch {
      // next source
    }
  }
  return null
}

/**
 * Download one artifact from the first source whose decompressed bytes match the SDK's
 * pinned SHA-256. Returns null when no source serves a valid file.
 */
export async function fetchVerifiedArtifact(
  variant: string,
  kind: VerifiedArtifactKind,
  log?: (msg: string) => void
): Promise<Buffer | null> {
  const expected = getExpectedArtifactHash(variant, kind)
  if (!expected) return null
  for (const url of getArtifactUrls(variant, kind)) {
    try {
      const raw = Buffer.from(await fetchWithTimeout(url))
      const out = decompress(raw)
      if (!out) throw new Error('brotli decompress failed')
      const data = Buffer.from(out)
      const actual = await sha256Hex(data)
      if (actual === expected) return data
      log?.(`artifact ${variant}/${kind}: hash mismatch from ${new URL(url).host}`)
    } catch (err) {
      log?.(
        `artifact ${variant}/${kind}: ${new URL(url).host} failed: ${(err as Error)?.message || String(err)}`
      )
    }
  }
  return null
}
